/**
 * @fileoverview Helper para generar archivos Excel del Informe de Órdenes de Producción
 */

import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { formatProductoNombreForExcel } from '../utils/productoInventarioHelper';

/**
 * Genera un archivo Excel con las órdenes de producción filtradas
 * @param {Array} ordenes - Array de órdenes de producción (con expand de cliente, producto, activa y finalizada)
 * @param {Map<string, Array>} materialesPorOrden - Mapa ordenId => materiales (reservas/solicitudes)
 * @param {boolean} soloReprocesos - Si true, indica que son solo reprocesos para el nombre del archivo
 * @returns {void}
 */
export function generateExcel(ordenes, materialesPorOrden = new Map(), soloReprocesos = false) {
  try {
    if (!ordenes || ordenes.length === 0) {
      throw new Error('No hay órdenes para exportar');
    }

    // Definir headers
    const headers = [
      'Número',
      'Cliente',
      'Fecha Inicio',
      'Fecha Entrega',
      'Cantidad Solicitada',
      'Trabajo Interno',
      'Tipo de Producto',
      'Nombre Producto',
      'Fecha Inicio Producción',
      'Fecha Finalización'
    ];

    // Transformar datos al formato requerido
    const rows = ordenes.map(orden => {
      // Obtener razón social del cliente
      // Primero intentar desde expand.cliente (forma estándar de PocketBase)
      let razonSocial = '';
      
      // Debug: verificar qué datos tenemos
      if (orden.expand?.cliente) {
        // Cliente expandido correctamente
        razonSocial = orden.expand.cliente.razon_social || '';
        if (!razonSocial) {
          // Si no hay razon_social, intentar otros campos o loggear
          console.warn(`Cliente expandido para orden ${orden.numero || orden.id} pero razon_social está vacío. Cliente completo:`, JSON.stringify(orden.expand.cliente, null, 2));
        }
      } else if (orden.cliente && typeof orden.cliente === 'object' && !Array.isArray(orden.cliente)) {
        // Si cliente está como objeto directo (no en expand)
        razonSocial = orden.cliente.razon_social || '';
      } else if (orden.cliente && typeof orden.cliente === 'string') {
        // Si cliente es solo un ID (no expandido) - esto es el problema
        console.warn(`⚠️ Cliente NO expandido para orden ${orden.numero || orden.id}. ID cliente: ${orden.cliente}. Esto indica que PocketBase no está expandiendo el cliente correctamente.`);
        razonSocial = ''; // Dejar vacío si no está expandido
      } else {
        // Caso donde no hay cliente asignado
        console.warn(`Orden ${orden.numero || orden.id} no tiene cliente asignado.`);
      }

      // Obtener nombre del producto (tipo de producto)
      let tipoProducto = '';
      if (orden.expand?.producto) {
        tipoProducto = orden.expand.producto.nombre || '';
      } else if (orden.producto && typeof orden.producto === 'object' && !Array.isArray(orden.producto)) {
        tipoProducto = orden.producto.nombre || '';
      }

      return [
        orden.numero || '',
        razonSocial,
        orden.fecha_inicio ? new Date(orden.fecha_inicio).toLocaleDateString() : '',
        orden.fecha_entrega ? new Date(orden.fecha_entrega).toLocaleDateString() : '',
        orden.cantidad_solicitada || 0,
        orden.trabajo_interno ? 'Sí' : 'No',
        tipoProducto,
        orden.nombre_producto || '',
        orden.expand?.activa?.fecha_estado
          ? new Date(orden.expand.activa.fecha_estado).toLocaleDateString()
          : '',
        orden.expand?.finalizada?.fecha_estado
          ? new Date(orden.expand.finalizada.fecha_estado).toLocaleDateString()
          : ''
      ];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    if (!ws || !ws['!ref']) {
      throw new Error('Error al crear la hoja de trabajo Excel');
    }

    applyStyles(ws);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Órdenes de Producción');

    if (materialesPorOrden && materialesPorOrden.size > 0) {
      materialesPorOrden.forEach((materiales, ordenId) => {
        if (!materiales || materiales.length === 0) return;

        const headersMateriales = [
          'Fecha de Creación',
          'Número de Orden',
          'Producto de Inventario',
          'Total',
          'Usuario',
          'Tipo',
          'Completado',
          'Inventario Local',
          'Comentario'
        ];

        const rowsMateriales = materiales.map((item) => {
          const fechaCreacion = item.created
            ? new Date(item.created).toLocaleString('es-ES', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'N/A';

          const numeroOrden = item.expand?.orden_produccion?.numero ?? item._ordenNumero ?? 'N/A';
          const productoInventario = formatProductoNombreForExcel(item.expand?.producto_inventario);
          const total = item.total || 0;
          const usuario =
            item.expand?.usuario?.name ||
            item.expand?.usuario?.email ||
            'N/A';
          const tipo = item.devolucion === true ? 'Devolución' : 'Solicitud';
          const completado = item.completado === true ? 'Sí' : 'No';
          const inventarioLocal = item.inventario_local !== false ? 'Sí' : 'No';
          const comentario = item.comentario || '';

          return [
            fechaCreacion,
            numeroOrden,
            productoInventario,
            total,
            usuario,
            tipo,
            completado,
            inventarioLocal,
            comentario
          ];
        });

        const wsMateriales = XLSX.utils.aoa_to_sheet([headersMateriales, ...rowsMateriales]);
        applyStyles(wsMateriales);

        const ordenNumero =
          materiales[0]?.expand?.orden_produccion?.numero ??
          materiales[0]?._ordenNumero ??
          ordenId;
        const baseName = `OP-${String(ordenNumero ?? ordenId)}`;
        const sheetName = baseName.length > 31 ? baseName.slice(0, 31) : baseName;
        XLSX.utils.book_append_sheet(wb, wsMateriales, sheetName);
      });
    }

    // Generar archivo y descargar usando file-saver (siguiendo patrón de ExcelExportService)
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fileName = soloReprocesos
      ? `Informe_OP_reprocesos_${new Date().toISOString().split('T')[0]}.xlsx`
      : `Informe_OP_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(blob, fileName);
  } catch (error) {
    console.error('Error generating Excel:', error);
    throw error;
  }
}

/**
 * Aplica estilos a la hoja de trabajo
 * @param {Object} ws - Hoja de trabajo
 */
function applyStyles(ws) {
  if (!ws || !ws['!ref']) {
    return; // No aplicar estilos si la hoja no es válida
  }

  // Definir ancho de columnas
  const colWidths = [
    { wch: 12 }, // Número
    { wch: 30 }, // Cliente
    { wch: 15 }, // Fecha Inicio
    { wch: 15 }, // Fecha Entrega
    { wch: 18 }, // Cantidad Solicitada
    { wch: 15 }, // Trabajo Interno
    { wch: 25 }, // Tipo de Producto
    { wch: 30 }, // Nombre Producto
    { wch: 22 }, // Fecha Inicio Producción
    { wch: 18 }  // Fecha Finalización
  ];

  ws['!cols'] = colWidths;

  // Decodificar rango para obtener dimensiones
  const headerRange = XLSX.utils.decode_range(ws['!ref']);
  if (!headerRange || !headerRange.s || !headerRange.e) {
    return; // No aplicar estilos si no se puede decodificar el rango
  }

  // Calcular altura de filas basada en el contenido
  const rowHeights = [];
  const minRowHeight = 15; // Altura mínima en puntos
  const lineHeight = 15; // Puntos por línea de texto
  const padding = 4; // Padding adicional

  // Aplicar estilos a todas las filas
  for (let row = headerRange.s.r; row <= headerRange.e.r; row++) {
    let maxLines = 1; // Mínimo 1 línea por celda

    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = ws[cellAddress];
      
      if (!cell) continue;

      // Convertir valor a string para calcular líneas
      const cellValue = cell.v !== undefined ? String(cell.v) : '';
      
      // Calcular cuántas líneas necesita el texto
      // Aproximadamente 10 caracteres por unidad de ancho de columna (wch)
      const colWidth = colWidths[col]?.wch || 10;
      const charsPerLine = colWidth * 10;
      const lines = Math.max(1, Math.ceil(cellValue.length / charsPerLine));
      
      maxLines = Math.max(maxLines, lines);

      // Configurar estilo de celda con wrapText
      if (!cell.s) cell.s = {};
      
      if (row === headerRange.s.r) {
        // Estilos para header
        cell.s = {
          font: { bold: true },
          fill: { fgColor: { rgb: 'E6E6FA' } },
          alignment: { 
            horizontal: 'center', 
            vertical: 'center',
            wrapText: true
          }
        };
      } else {
        // Estilos para datos
        cell.s = {
          alignment: { 
            horizontal: 'left', 
            vertical: 'top',
            wrapText: true
          }
        };
      }
    }

    // Calcular altura de fila basada en el máximo de líneas necesarias
    const rowHeight = Math.max(minRowHeight, maxLines * lineHeight + padding);
    rowHeights[row] = { hpt: rowHeight };
  }

  // Aplicar alturas de filas
  ws['!rows'] = rowHeights;
}
